import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingBag, Plug, Warehouse, RefreshCw, Trash2, Webhook, Zap, AlertTriangle, Copy, KeyRound, Percent } from 'lucide-react';
import { checkMarginGuard } from '@/components/ChannelPricingRules';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const copyText = (t, label) => { navigator.clipboard.writeText(t); toast.success(`${label} copied`); };

export default function CarrefourIntegrationPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshToken, setRefreshToken] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [locations, setLocations] = useState([]);
  const [newMap, setNewMap] = useState({ app_location_id: '', pos_id: '', stock_buffer: 0 });
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [savingPricing, setSavingPricing] = useState(false);
  const [previewPrice, setPreviewPrice] = useState({ selling: '40', compare: '60' });

  const load = async () => {
    try {
      const [s, l] = await Promise.all([
        axios.get(`${API}/carrefour/status`, auth()),
        axios.get(`${API}/enterprise/locations`, auth()),
      ]);
      setStatus(s.data);
      const d = s.data || {};
      setPricing({
        sync_discounted_price: !!d.sync_discounted_price,
        markup_type: d.markup_type || 'none',
        markup_value: d.markup_value || '',
        shipping_fee_enabled: !!d.shipping_fee_enabled,
        shipping_fee_threshold: d.shipping_fee_threshold || '',
        shipping_fee_amount: d.shipping_fee_amount || '',
      });
      setLocations(l.data.locations || l.data || []);
    } catch (e) {
      toast.error('Failed to load Carrefour status');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const connect = async () => {
    if (!refreshToken.trim()) { toast.error('Paste your Carrefour refresh token'); return; }
    setConnecting(true);
    try {
      const r = await axios.post(`${API}/carrefour/connect`, { refresh_token: refreshToken.trim() }, auth());
      if (r.data.verified) toast.success('Connected to Carrefour — token verified');
      else toast.warning(r.data.warning || 'Saved, but could not verify the token from the server yet');
      setRefreshToken('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Connection failed');
    } finally {
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Carrefour? Stock sync and order webhooks will stop.')) return;
    try { await axios.post(`${API}/carrefour/disconnect`, {}, auth()); toast.success('Disconnected'); load(); }
    catch { toast.error('Failed to disconnect'); }
  };

  const saveSetting = async (patch) => {
    try {
      await axios.put(`${API}/carrefour/settings`, patch, auth());
      setStatus((s) => ({ ...s, ...patch }));
      toast.success('Settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
  };

  const savePricing = async () => {
    setSavingPricing(true);
    await saveSetting({
      sync_discounted_price: !!pricing.sync_discounted_price,
      markup_type: pricing.markup_type || 'none',
      markup_value: parseFloat(pricing.markup_value) || 0,
      shipping_fee_enabled: !!pricing.shipping_fee_enabled,
      shipping_fee_threshold: parseFloat(pricing.shipping_fee_threshold) || 0,
      shipping_fee_amount: parseFloat(pricing.shipping_fee_amount) || 0,
    });
    setSavingPricing(false);
  };

  const computePreview = () => {
    const selling = parseFloat(previewPrice.selling);
    if (!pricing || !selling || selling <= 0) return null;
    const compare = parseFloat(previewPrice.compare) || 0;
    const mv = parseFloat(pricing.markup_value) || 0;
    const mark = (x) => (mv > 0 && pricing.markup_type === 'percent' ? x * (1 + mv / 100) : (mv > 0 && pricing.markup_type === 'fixed' ? x + mv : x));
    let price = mark(selling);
    let ship = 0;
    const th = parseFloat(pricing.shipping_fee_threshold) || 0;
    const amt = parseFloat(pricing.shipping_fee_amount) || 0;
    if (pricing.shipping_fee_enabled && th > 0 && amt > 0 && price < th) ship = amt;
    price = Math.round((price + ship) * 100) / 100;
    if (pricing.sync_discounted_price && compare > selling) {
      const original = Math.round((mark(compare) + ship) * 100) / 100;
      if (original > price) return { price: original, discounted: price, ship };
    }
    return { price, ship };
  };

  const addMapping = async () => {
    if (!newMap.app_location_id) { toast.error('Select a warehouse'); return; }
    try {
      await axios.post(`${API}/carrefour/mappings`, newMap, auth());
      toast.success('Warehouse mapped');
      setNewMap({ app_location_id: '', pos_id: '', stock_buffer: 0 });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not add mapping'); }
  };

  const deleteMapping = async (id) => {
    try { await axios.delete(`${API}/carrefour/mappings/${id}`, auth()); toast.success('Mapping removed'); load(); }
    catch { toast.error('Could not remove'); }
  };

  const syncStock = async () => {
    setSyncing(true);
    try {
      const guard = await checkMarginGuard('carrefour');
      if (!guard.proceed) { setSyncing(false); return; }
      const start = await axios.post(`${API}/carrefour/sync-stock`, {}, auth());
      const jobId = start.data.job_id;
      if (!jobId) throw new Error('No job id');
      if (start.data.message) toast.info(start.data.message);
      // Poll the background job — big catalogs take minutes
      for (let i = 0; i < 100; i++) {
        await new Promise(r => setTimeout(r, 3000));
        const jr = await axios.get(`${API}/carrefour/sync-job/${jobId}`, auth());
        const job = jr.data;
        if (job.progress) setSyncProgress(job.progress);
        if (job.status === 'running') continue;
        const res = (job.results || [])[0] || {};
        if (job.status === 'failed') {
          toast.error(job.error || (res.failed || [])[0]?.message || 'Sync failed');
        } else {
          toast.success(`Price & stock sent — ${res.pushed || 0} update(s)${res.skipped_no_price ? `, ${res.skipped_no_price} skipped (no selling price)` : ''}`);
        }
        load();
        setSyncing(false);
        return;
      }
      toast.info('Sync is still running in the background — check back in a few minutes');
    } catch (e) { toast.error(e.response?.data?.detail || 'Sync failed'); }
    finally { setSyncing(false); setSyncProgress(null); }
  };

  if (loading) return <div className="p-8 text-wt-text-muted" data-testid="carrefour-loading">Loading…</div>;
  const connected = status?.connected;
  const webhookUrl = `${API}/carrefour/webhook/${status?.company_id || ''}`;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="carrefour-page">
      <button onClick={() => navigate('/integrations')} className="flex items-center gap-2 text-sm text-wt-text-muted hover:text-wt-text-primary" data-testid="carrefour-back-btn">
        <ArrowLeft className="h-4 w-4" /> Back to Integrations
      </button>

      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
          <ShoppingBag className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-wt-text-primary">Carrefour UAE</h1>
          <p className="text-sm text-wt-text-muted">MAF Marketplace Partner API — price &amp; stock sync, real-time order webhooks, fulfilment pushes.</p>
        </div>
        {connected && <Badge className="ml-auto bg-green-500/15 text-green-500 border border-green-500/30" data-testid="carrefour-connected-badge">Connected</Badge>}
      </div>

      {connected && status?.needs_reauth && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 flex items-start gap-2 text-xs text-red-300" data-testid="carrefour-reauth-note">
          <KeyRound className="h-4 w-4 mt-0.5 shrink-0" />
          <span>The refresh token expired or was already used. Generate a new one in Seller Portal → Account Settings → API Integration → GET REFRESH TOKEN and reconnect below.</span>
        </div>
      )}
      {connected && status?.verified === false && !status?.needs_reauth && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 flex items-start gap-2 text-xs text-amber-300" data-testid="carrefour-unverified-note">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>Credentials saved, but the server could not reach the Carrefour MKP API yet. Confirm the API host with your Carrefour integration contact and ask them to whitelist the server IP. Sync starts automatically once reachable.</span>
        </div>
      )}

      {/* Connection */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="rounded-xl border border-wt-border bg-wt-surface p-5 space-y-4">
        <div className="flex items-center gap-2"><Plug className="h-4 w-4 text-wt-gold" /><h2 className="font-semibold text-wt-text-primary">Connection</h2></div>
        {!connected ? (
          <div className="space-y-3">
            <div>
              <Label>Refresh Token</Label>
              <Input type="password" value={refreshToken} onChange={(e) => setRefreshToken(e.target.value)} placeholder="Seller Portal → Account Settings → API Integration" data-testid="carrefour-refreshtoken-input" className="sm:max-w-md" />
            </div>
            <p className="text-xs text-wt-text-muted">Generate the refresh token in the Seller Portal: <b>Account Settings → API Integration → GET REFRESH TOKEN</b>. We exchange it for a 24h access token and keep it rotated automatically.</p>
            <Button onClick={connect} disabled={connecting} className="bg-wt-gold hover:bg-wt-gold-hover text-black" data-testid="carrefour-connect-btn">
              {connecting ? 'Connecting…' : 'Connect Carrefour'}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <p className="text-sm text-wt-text-muted">API host: <span className="text-wt-text-primary">{status.api_base}</span></p>
              <Button variant="outline" onClick={disconnect} className="border-red-500/30 text-red-400" data-testid="carrefour-disconnect-btn">Disconnect</Button>
            </div>
            {(status.needs_reauth || !status.verified) && (
              <div className="flex gap-2 items-end flex-wrap">
                <Button onClick={async () => {
                  setConnecting(true);
                  try {
                    await axios.post(`${API}/carrefour/verify`, {}, auth());
                    toast.success('Carrefour connection verified');
                    load();
                  } catch (e) { toast.error(e.response?.data?.detail || 'Verification failed'); }
                  finally { setConnecting(false); }
                }} disabled={connecting} variant="outline" className="border-wt-gold/50 text-wt-gold" data-testid="carrefour-verify-btn">
                  {connecting ? 'Verifying…' : 'Verify Now'}
                </Button>
                <div className="flex-1 min-w-[220px]">
                  <Label>New Refresh Token</Label>
                  <Input type="password" value={refreshToken} onChange={(e) => setRefreshToken(e.target.value)} placeholder="Paste a freshly generated token" data-testid="carrefour-retoken-input" />
                </div>
                <Button onClick={connect} disabled={connecting} className="bg-wt-gold hover:bg-wt-gold-hover text-black" data-testid="carrefour-retoken-btn">{connecting ? 'Saving…' : 'Update Token'}</Button>
              </div>
            )}
          </div>
        )}
      </motion.div>

      {connected && (
        <>
          {/* Webhook onboarding */}
          <div className="rounded-xl border border-wt-border bg-wt-surface p-5 space-y-3">
            <div className="flex items-center gap-2"><Webhook className="h-4 w-4 text-wt-gold" /><h2 className="font-semibold text-wt-text-primary">Order Webhook (give this to Carrefour)</h2></div>
            <p className="text-xs text-wt-text-muted">Carrefour pushes every order event (Ready, Exported, Dispatched, Delivered, returns…) to this endpoint. Send these three values to your Carrefour integration contact during onboarding — auth type is <b>Basic Authentication</b>.</p>
            <div className="space-y-2 text-sm">
              {[['Callback URL', webhookUrl, 'carrefour-webhook-url'], ['Username', status.webhook_username || '—', 'carrefour-webhook-user'], ['Password', status.webhook_password || '—', 'carrefour-webhook-pass']].map(([label, val, tid]) => (
                <div key={tid} className="flex items-center gap-2 rounded-lg border border-wt-border bg-wt-bg px-3 py-2">
                  <span className="text-wt-text-muted text-xs w-24 shrink-0">{label}</span>
                  <code className="flex-1 truncate text-wt-text-primary text-xs" data-testid={tid}>{val}</code>
                  <button onClick={() => copyText(val, label)} className="text-wt-text-muted hover:text-wt-gold" data-testid={`${tid}-copy`}><Copy className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
            {status.last_webhook && (
              <p className="text-xs text-wt-text-muted" data-testid="carrefour-last-webhook">Last event received: <span className="text-wt-text-primary">{status.last_webhook.event}</span> for order {status.last_webhook.order_code} · {new Date(status.last_webhook.created_at).toLocaleString()}</p>
            )}
          </div>

          {/* Automation settings */}
          <div className="rounded-xl border border-wt-border bg-wt-surface p-5 space-y-4">
            <div className="flex items-center gap-2"><Zap className="h-4 w-4 text-wt-gold" /><h2 className="font-semibold text-wt-text-primary">Order Automation</h2></div>
            <div className="flex items-center justify-between">
              <div><p className="text-sm text-wt-text-primary">Auto-accept new orders (PACK)</p><p className="text-xs text-wt-text-muted">Automatically accept &amp; pack incoming Carrefour orders the moment the webhook arrives.</p></div>
              <Switch checked={!!status.auto_accept} onCheckedChange={(v) => saveSetting({ auto_accept: v })} data-testid="carrefour-auto-accept-switch" />
            </div>
            <div className="border-t border-wt-border pt-4 flex items-center justify-between">
              <div><p className="text-sm text-wt-text-primary">Sync orders to Shopify</p><p className="text-xs text-wt-text-muted">Also create each Carrefour order on Shopify. The Carrefour order number stays the main number everywhere.</p></div>
              <Switch checked={!!status.sync_to_shopify} onCheckedChange={(v) => saveSetting({ sync_to_shopify: v })} data-testid="carrefour-shopify-sync-switch" />
            </div>
            <div className="border-t border-wt-border pt-4 flex items-center justify-between gap-3">
              <div><p className="text-sm text-wt-text-primary">Default carrier</p><p className="text-xs text-wt-text-muted">Used on the automatic SHIP push. "Self" = your own drivers (no AWB needed); iMile/Aramex require an AWB.</p></div>
              <Select value={status.default_carrier || 'Self'} onValueChange={(v) => saveSetting({ default_carrier: v })}>
                <SelectTrigger className="w-32" data-testid="carrefour-carrier-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['Self', 'iMile', 'Aramex'].map((c) => <SelectItem key={c} value={c} data-testid={`carrefour-carrier-${c}`}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Pricing rules */}
          {pricing && (
            <div className="rounded-xl border border-wt-border bg-wt-surface p-5 space-y-4" data-testid="carrefour-pricing-card">
              <div className="flex items-center gap-2"><Percent className="h-4 w-4 text-wt-gold" /><h2 className="font-semibold text-wt-text-primary">Price Sync &amp; Markup</h2></div>

              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm text-wt-text-primary">Sync compare-at price as Carrefour Price</p>
                  <p className="text-xs text-wt-text-muted">Your <b>compare-at price</b> goes to Carrefour's <b>Price</b> field and your selling price becomes the <b>Discount Price</b> (always active, no dates). Products without a compare-at price send the selling price as the Price only.</p>
                </div>
                <Switch checked={!!pricing.sync_discounted_price} onCheckedChange={(v) => setPricing((p) => ({ ...p, sync_discounted_price: v }))} data-testid="carrefour-discount-sync-switch" />
              </div>

              <div className="border-t border-wt-border pt-4 space-y-2">
                <p className="text-sm text-wt-text-primary">Price markup</p>
                <p className="text-xs text-wt-text-muted">Increase prices sent to Carrefour (your app prices stay unchanged). Applied to both Price and Discount Price.</p>
                <div className="flex items-center gap-3">
                  <Select value={pricing.markup_type || 'none'} onValueChange={(v) => setPricing((p) => ({ ...p, markup_type: v }))}>
                    <SelectTrigger className="w-44" data-testid="carrefour-markup-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none" data-testid="carrefour-markup-none">No markup</SelectItem>
                      <SelectItem value="percent" data-testid="carrefour-markup-percent">Percentage (%)</SelectItem>
                      <SelectItem value="fixed" data-testid="carrefour-markup-fixed">Fixed amount (AED)</SelectItem>
                    </SelectContent>
                  </Select>
                  {pricing.markup_type !== 'none' && (
                    <Input type="number" min="0" step="0.01" className="w-32" value={pricing.markup_value}
                      onChange={(e) => setPricing((p) => ({ ...p, markup_value: e.target.value }))}
                      placeholder={pricing.markup_type === 'percent' ? 'e.g. 10' : 'e.g. 15'} data-testid="carrefour-markup-value-input" />
                  )}
                </div>
              </div>

              <div className="border-t border-wt-border pt-4 space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm text-wt-text-primary">Add shipping fee to low-priced products</p>
                    <p className="text-xs text-wt-text-muted">If the price <b>after markup</b> is below the threshold, a fixed shipping amount is added on top.</p>
                  </div>
                  <Switch checked={!!pricing.shipping_fee_enabled} onCheckedChange={(v) => setPricing((p) => ({ ...p, shipping_fee_enabled: v }))} data-testid="carrefour-shipfee-switch" />
                </div>
                {pricing.shipping_fee_enabled && (
                  <div className="flex items-center gap-2 text-sm text-wt-text-muted flex-wrap">
                    <span>If price below AED</span>
                    <Input type="number" min="0" step="0.01" className="w-24" value={pricing.shipping_fee_threshold}
                      onChange={(e) => setPricing((p) => ({ ...p, shipping_fee_threshold: e.target.value }))} placeholder="50" data-testid="carrefour-shipfee-threshold-input" />
                    <span>add AED</span>
                    <Input type="number" min="0" step="0.01" className="w-24" value={pricing.shipping_fee_amount}
                      onChange={(e) => setPricing((p) => ({ ...p, shipping_fee_amount: e.target.value }))} placeholder="35" data-testid="carrefour-shipfee-amount-input" />
                  </div>
                )}
              </div>

              <div className="border-t border-wt-border pt-4 space-y-2 rounded-lg">
                <p className="text-xs text-wt-text-muted font-medium">Preview with a sample product</p>
                <div className="flex items-center gap-2 text-sm text-wt-text-muted flex-wrap">
                  <span>Selling price</span>
                  <Input type="number" min="0" className="w-24" value={previewPrice.selling} onChange={(e) => setPreviewPrice((v) => ({ ...v, selling: e.target.value }))} data-testid="carrefour-preview-selling-input" />
                  <span>Compare-at</span>
                  <Input type="number" min="0" className="w-24" value={previewPrice.compare} onChange={(e) => setPreviewPrice((v) => ({ ...v, compare: e.target.value }))} data-testid="carrefour-preview-compare-input" />
                </div>
                {(() => {
                  const pv = computePreview();
                  if (!pv) return null;
                  return (
                    <p className="text-sm" data-testid="carrefour-pricing-preview">
                      <span className="text-wt-text-muted">Carrefour gets → </span>
                      <span className="text-wt-text-primary font-semibold">Price AED {pv.price.toFixed(2)}</span>
                      {pv.discounted != null && <span className="text-wt-gold font-semibold"> · Discount Price AED {pv.discounted.toFixed(2)}</span>}
                      {pv.ship > 0 && <span className="text-xs text-wt-text-muted"> (includes AED {pv.ship} shipping)</span>}
                    </p>
                  );
                })()}
              </div>

              <Button size="sm" onClick={savePricing} disabled={savingPricing} className="bg-wt-gold hover:bg-wt-gold-hover text-black" data-testid="carrefour-save-pricing-btn">
                {savingPricing ? 'Saving…' : 'Save Pricing Rules'}
              </Button>
            </div>
          )}

          {/* Warehouse mappings */}
          <div className="rounded-xl border border-wt-border bg-wt-surface p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><Warehouse className="h-4 w-4 text-wt-gold" /><h2 className="font-semibold text-wt-text-primary">Price &amp; Stock Sync — Warehouses</h2></div>
              <Button size="sm" onClick={syncStock} disabled={syncing} className="bg-wt-gold hover:bg-wt-gold-hover text-black" data-testid="carrefour-sync-stock-btn">
                <RefreshCw className={`h-4 w-4 mr-1 ${syncing ? 'animate-spin' : ''}`} /> Sync Price &amp; Stock Now
              </Button>
            </div>
            <p className="text-xs text-wt-text-muted">If an offer has a mapped Carrefour warehouse, enter its <b>POS ID</b> (e.g. MWH210012) so stock is pushed per warehouse; leave POS ID empty for offers without a mapped warehouse (quantities are combined). Price comes from each product's selling price, adjusted by the <b>Price Sync &amp; Markup</b> rules above — SKUs without a selling price are skipped (Carrefour requires price on every update).</p>

            {syncing && (
              <div className="rounded-lg border border-wt-gold/30 bg-wt-gold/5 p-3 space-y-2" data-testid="carrefour-sync-progress">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-wt-text-primary font-medium">
                    {syncProgress
                      ? `Pushing batch ${Math.min(syncProgress.done_batches + 1, syncProgress.total_batches)} of ${syncProgress.total_batches} — ${syncProgress.pushed}/${syncProgress.total_updates} updates sent${syncProgress.failed ? `, ${syncProgress.failed} failed` : ''}`
                      : 'Preparing sync — computing price & stock updates…'}
                  </span>
                  <span className="text-wt-gold font-semibold" data-testid="carrefour-sync-progress-pct">
                    {syncProgress ? `${Math.round((syncProgress.done_batches / Math.max(syncProgress.total_batches, 1)) * 100)}%` : ''}
                  </span>
                </div>
                <div className="h-2 rounded-full bg-wt-bg overflow-hidden">
                  <div
                    className="h-full rounded-full bg-wt-gold transition-all duration-500"
                    style={{ width: syncProgress ? `${Math.max(4, Math.round((syncProgress.done_batches / Math.max(syncProgress.total_batches, 1)) * 100))}%` : '4%' }}
                    data-testid="carrefour-sync-progress-bar"
                  />
                </div>
              </div>
            )}

            <div className="rounded-lg border border-wt-border p-3 space-y-3 bg-wt-bg">
              <div className="grid sm:grid-cols-3 gap-3">
                <div>
                  <Label>Opswarex Warehouse</Label>
                  <Select value={newMap.app_location_id} onValueChange={(v) => setNewMap((m) => ({ ...m, app_location_id: v }))}>
                    <SelectTrigger data-testid="carrefour-map-location-select"><SelectValue placeholder="Select warehouse" /></SelectTrigger>
                    <SelectContent>{locations.map((l) => <SelectItem key={l.id} value={l.id} data-testid={`carrefour-loc-${l.id}`}>{l.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Carrefour POS ID (optional)</Label>
                  <Input value={newMap.pos_id} onChange={(e) => setNewMap((m) => ({ ...m, pos_id: e.target.value }))} placeholder="e.g. MWH210012" data-testid="carrefour-map-pos-input" />
                </div>
                <div>
                  <Label>Stock Buffer</Label>
                  <Input type="number" min="0" value={newMap.stock_buffer} onChange={(e) => setNewMap((m) => ({ ...m, stock_buffer: Number(e.target.value) }))} data-testid="carrefour-map-buffer-input" />
                </div>
              </div>
              <Button size="sm" onClick={addMapping} className="bg-wt-gold hover:bg-wt-gold-hover text-black" data-testid="carrefour-add-mapping-btn">Add Mapping</Button>
            </div>

            <div className="space-y-2" data-testid="carrefour-mappings-list">
              {(status.mappings || []).length === 0 && <p className="text-sm text-wt-text-muted">No warehouse mappings yet. Add one above to start syncing price &amp; stock.</p>}
              {(status.mappings || []).map((m) => (
                <div key={m.id} className="flex items-center justify-between rounded-lg border border-wt-border p-3" data-testid={`carrefour-mapping-${m.id}`}>
                  <div className="text-sm">
                    <p className="text-wt-text-primary font-medium">{m.app_location_name}{m.pos_id ? <span className="ml-2 text-xs text-blue-400">POS {m.pos_id}</span> : <span className="ml-2 text-xs text-wt-text-muted">no POS (single)</span>}</p>
                    <p className="text-wt-text-muted text-xs">{m.stock_buffer ? `buffer ${m.stock_buffer}` : 'no buffer'}{m.stock_cap ? ` · cap ${m.stock_cap}` : ''}</p>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => deleteMapping(m.id)} data-testid={`carrefour-del-mapping-${m.id}`}><Trash2 className="h-4 w-4 text-red-400" /></Button>
                </div>
              ))}
            </div>
            {status.last_sync && <p className="text-xs text-wt-text-muted" data-testid="carrefour-last-sync">Last sync: {new Date(status.last_sync.created_at).toLocaleString()} ({status.last_sync.trigger})</p>}
          </div>

          <div className="rounded-xl border border-wt-border bg-wt-surface p-5">
            <h2 className="font-semibold text-wt-text-primary mb-1">How fulfilment flows</h2>
            <p className="text-sm text-wt-text-muted">Orders arrive automatically via the webhook above — there is no manual order pull on Carrefour's API. Work them in <button onClick={() => navigate('/orders')} className="text-wt-gold underline" data-testid="carrefour-goto-orders">Orders</button> as usual: Accept/Pack (or auto), mark <b>Ready to Ship</b> → we push PACK + SHIP to Carrefour; your driver's delivery with POD → we push DELIVER with the proof-of-delivery PDF. Manual buttons stay available on each order.</p>
          </div>
        </>
      )}
    </div>
  );
}
